import React, { useState } from "react";
import { Flex, Text, Button, Tag } from "components/atoms";
import { COLOR_SYSTEM } from "components/atoms";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import styled from "styled-components";

// 포인트 상품 인터페이스
interface PointProduct {
  id: number;
  name: string;
  price: number;
  basePoints: number;
  bonusRate: number;
  totalPoints: number;
  isRecommended: boolean;
  description?: string;
}

// 컴포넌트 Props 인터페이스
interface PointsChargeSlideProps {
  products: PointProduct[];
  onSelect: (product: PointProduct) => void;
  loading?: boolean;
}

// Styled Components
const SlideContainer = styled.div`
  width: 100%;
  height: 100%;
  background: ${COLOR_SYSTEM.get("Gray")[0]};
  display: flex;
  flex-direction: column;
`;

const HeaderSection = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")[100]};
  background: ${COLOR_SYSTEM.get("Gray")[0]};
  position: sticky;
  top: 0;
  z-index: 10;
`;

const ContentSection = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const ProductCard = styled.div<{ selected?: boolean; disabled?: boolean }>`
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid
    ${({ selected }) => (selected ? COLOR_SYSTEM.get("Blue")[400] : COLOR_SYSTEM.get("Gray")[100])};
  border-radius: 12px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  background: ${({ selected }) =>
    selected ? COLOR_SYSTEM.get("Blue")[10] : COLOR_SYSTEM.get("Gray")[0]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ disabled }) =>
      disabled ? COLOR_SYSTEM.get("Gray")[100] : COLOR_SYSTEM.get("Blue")[300]};
  }
`;

const ProductHeader = styled.div`
  margin-bottom: 12px;
`;

const ProductDetails = styled.div`
  margin-bottom: 16px;
`;

const FooterSection = styled.div`
  padding: 20px;
  background: ${COLOR_SYSTEM.get("Gray")[50]};
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")[100]};
`;

const InfoBox = styled.div`
  padding: 16px;
  background: ${COLOR_SYSTEM.get("Gray")[50]};
  border-radius: 8px;
  margin-top: 20px;
`;

const PointsChargeSlide: React.FC<PointsChargeSlideProps> = ({
  products,
  onSelect,
  loading = false,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<PointProduct | null>(null);

  const handleProductSelect = (product: PointProduct) => {
    if (loading) return;
    setSelectedProduct(product);
  };

  const handleConfirm = () => {
    if (selectedProduct && !loading) {
      onSelect(selectedProduct);
    }
  };

  return (
    <SlideContainer>
      {/* 헤더 섹션 */}
      <HeaderSection>
        <Flex alignItems="center" justifyContent="space-between">
          <Text textType="H2">포인트 충전</Text>
          <Button
            size="Small"
            type="Icon"
            variant="Ghost"
            color="Secondary"
            icon="X"
            onClick={() => SlideManager.hide()}
          />
        </Flex>
      </HeaderSection>

      {/* 콘텐츠 섹션 */}
      <ContentSection>
        <div style={{ marginBottom: "20px" }}>
          <Text textType="H4">충전할 포인트를 선택하세요</Text>
        </div>

        {products.map((product) => (
          <ProductCard
            key={product.id}
            selected={selectedProduct?.id === product.id}
            disabled={loading}
            onClick={() => handleProductSelect(product)}
          >
            {/* 상품 헤더 */}
            <ProductHeader>
              <Flex alignItems="center" justifyContent="space-between">
                <div>
                  <Text textType="H3" color={COLOR_SYSTEM.get("Blue")[600]}>
                    {product.totalPoints.toLocaleString()}P
                  </Text>
                </div>
                {product.isRecommended && (
                  <Tag size="Small" variant="Contained" color="Blue">
                    인기
                  </Tag>
                )}
              </Flex>
            </ProductHeader>

            {/* 상품 상세 정보 */}
            <ProductDetails>
              {product.bonusRate > 0 && (
                <Flex alignItems="center" gap="8px" style={{ marginBottom: "8px" }}>
                  <Tag size="Small" variant="Tinted" color="Green">
                    +{product.bonusRate}% 보너스
                  </Tag>
                  <Text textType="Body2Medium" color={COLOR_SYSTEM.get("Green")[600]}>
                    {(product.totalPoints - product.basePoints).toLocaleString()}P 추가 적립
                  </Text>
                </Flex>
              )}
              <Flex alignItems="flex-end" justifyContent="space-between">
                <div>
                  <Text textType="Body2Medium" color={COLOR_SYSTEM.get("Gray")[600]}>
                    {product.bonusRate > 0
                      ? `기본 ${product.basePoints.toLocaleString()}P + 보너스 ${(
                          product.totalPoints - product.basePoints
                        ).toLocaleString()}P`
                      : product.description || `기본 ${product.basePoints.toLocaleString()}P`}
                  </Text>
                </div>
                <Text textType="H3" color={COLOR_SYSTEM.get("Gray")[800]}>
                  {product.price.toLocaleString()}원
                </Text>
              </Flex>
            </ProductDetails>
          </ProductCard>
        ))}

        {/* 안내 메시지 */}
        <InfoBox>
          <Text
            textType="Body2Medium"
            color={COLOR_SYSTEM.get("Gray")[600]}
            style={{ whiteSpace: "pre-line" }}
          >
            {`• 10원 = 1포인트 기본 환율로 충전
• 금액이 클수록 더 많은 보너스 포인트 제공
• 결제 완료 즉시 포인트 충전`}
          </Text>
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <Text
              textType="Body2"
              color={COLOR_SYSTEM.get("Blue")[500]}
              style={{ 
                cursor: 'pointer',
                textDecoration: 'underline',
                display: 'inline-block'
              }}
              onClick={() => window.open('/mypage/billing?tab=pricing', '_blank')}
            >
              전체 요금 정책 보기
            </Text>
          </div>
        </InfoBox>
      </ContentSection>

      {/* 하단 버튼 섹션 */}
      <FooterSection>
        {selectedProduct && (
          <Flex alignItems="center" justifyContent="space-between" style={{ marginBottom: "16px" }}>
            <Text textType="Body1Medium">선택한 포인트</Text>
            <Text textType="H4" color={COLOR_SYSTEM.get("Blue")[600]}>
              {selectedProduct.totalPoints.toLocaleString()}P
            </Text>
          </Flex>
        )}

        <Button
          size="Large"
          type="Text"
          variant="Contained"
          color="Primary"
          onClick={handleConfirm}
          disabled={!selectedProduct || loading}
          style={{ width: "100%" }}
        >
          {loading
            ? "처리중..."
            : selectedProduct
            ? `${selectedProduct.price.toLocaleString()}원 결제하기`
            : "포인트를 선택하세요"}
        </Button>
      </FooterSection>
    </SlideContainer>
  );
};

export default PointsChargeSlide;
